import { useAuth } from '@myosh/myosh-login';
import {
  type DialogRef,
  DynamicForm,
  type DynamicFormButtonSetting,
  type DynamicFormRef,
  type JsonDataItem,
  type PreSubmitHandler,
} from '@myosh/odin-components';
import { cloneDeep } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import type { UserMutationResponse, UserResult } from '../@types/users';
import ConfirmationDialog from '../components/common/confirmation-dialog.component';
import { FormTitle } from '../components/form/form-title.component';
import { getRegistrationFormSettings } from '../components/registration/registration-form-settings';
import { useApiLogger } from '../hooks/use-api-logger';
import usePerformingSaveToast from '../hooks/use-performing-save-toast';
import i18next from '../i18n';
import { useAddUserMutation } from '../redux/services/user';
import { showError, showSuccess } from '../services/notification.service';
import { forceAssert } from '../utils/type-guards';
import { UserFields } from '../utils/user-config';

export const registerButton: DynamicFormButtonSetting = {
  name: 'register',
  text: i18next.t('register'),
  htmlType: 'submit',
  type: 'primary',
  variant: 'alternative',
};

function RegistrationPage() {
  const registrationFormSettings = getRegistrationFormSettings();

  const dynamicFormReference = useRef<DynamicFormRef>(null);
  const dynamicFormData = useRef<JsonDataItem | undefined>(undefined);
  const registrationConfirmationModalRef = useRef<DialogRef>(null);
  const dynamicFormId = useRef(v4());

  const { t } = useTranslation();
  const { logout } = useAuth();
  const log = useApiLogger();

  const [addUser] = useAddUserMutation();
  const { showPerformingSaveToast, hidePerformingSaveToast } = usePerformingSaveToast({});

  const [formButtons, setFormButtons] = useState<Array<DynamicFormButtonSetting>>([]);

  const onFormSubmit = useCallback((data: JsonDataItem) => {
    const userData = {
      [UserFields.firstName]: data.firstName as string,
      [UserFields.lastName]: data.lastName as string,
      [UserFields.email]: data.email as string,
      [UserFields.password]: data.password as string,
      [UserFields.retypePassword]: data.retypePassword as string,
      [UserFields.loginRequired]: true,
      localeCode: 'en-AU',
    };

    saveUserData(userData, (response) => {
      const convertedResponse = forceAssert<UserMutationResponse>(response);
      if (convertedResponse?.result) {
        showSuccessSaveToast();
      } else if (convertedResponse?.error) {
        handleSaveFailedNotifications(convertedResponse.error);
      }
    });
  }, []);

  const showErrorToast = () => {
    showError(t('user-details-save-failed'));
  };

  const logoutUser = useCallback(() => {
    setTimeout(async () => await logout(), 3000);
  }, [logout]);

  const showSuccessSaveToast = () => {
    hidePerformingSaveToast();
    showSuccess(t('register-user-success', 'User registered, redirecting to login page..'));
    logoutUser();
  };

  const saveUserData = (userData: Partial<UserResult>, successCallback: (response?: unknown) => void) => {
    showPerformingSaveToast();
    return addUser(userData)
      .unwrap()
      .then(successCallback)
      .catch((error) => {
        handleSaveFailedNotifications(error);
      });
  };

  const handleSaveFailedNotifications = useCallback((error: unknown) => {
    hidePerformingSaveToast();
    showErrorToast();
    log('Failed to create user', { error: error });
  }, []);

  const onPreSubmit = useCallback<PreSubmitHandler>((_preSubmitData, formData) => {
    // Ensure onFormSubmit is called with the 'form data' and not the 'data proxy raw data'
    // Should be removed when MYOSH-5887 is implemented
    return dynamicFormData.current ?? formData;
  }, []);

  const handleRegisterBtnClick = useCallback(() => {
    dynamicFormData.current = dynamicFormReference.current?.getData();

    registrationConfirmationModalRef.current?.show();
  }, []);

  const onRegistrationConfirm = () => {
    dynamicFormReference.current?.submitForm();
  };

  useEffect(() => {
    const newFormButtons: Array<DynamicFormButtonSetting> = [];

    const _registerButton = cloneDeep(registerButton);
    _registerButton.onClick = handleRegisterBtnClick;
    _registerButton.htmlType = 'button';
    newFormButtons.push(_registerButton);

    setFormButtons(newFormButtons);
  }, [handleRegisterBtnClick]);

  return (
    <>
      <div className="mx-auto flex h-fit w-[50%] flex-col py-4">
        <div className="mb-2 flex">
          <FormTitle title={'Registration'} />
        </div>
        <div className={'relative flex flex-grow flex-row'}>
          <DynamicForm
            ref={dynamicFormReference}
            dynamicFormId={dynamicFormId.current}
            data={{ hierarchies: {}, loginRequired: true }}
            settings={registrationFormSettings}
            readOnly={false}
            buttons={formButtons}
            buttonsLocation={0}
            buttonsPosition={1}
            onPreSubmit={onPreSubmit}
            onSubmit={onFormSubmit}
          />
        </div>
      </div>
      <ConfirmationDialog
        ref={registrationConfirmationModalRef}
        header={t('warning')}
        onConfirm={onRegistrationConfirm}
        confirmButtonText={t('continue')}
        hasCancelButton
      >
        <p>{t('register-user-confirmation', 'You are about to complete your registration. Do you want to proceed?')}</p>
      </ConfirmationDialog>
    </>
  );
}

export default RegistrationPage;
